import { filterClasses } from "@swivl-mono/helpers";
import React from "react";
import { IoAlertCircleOutline, IoBanOutline, IoCheckmark, IoCheckmarkDone, IoEyeOffOutline, IoRemoveCircleOutline } from "react-icons/io5";

import { DeliveryTickState } from "./deliveryTickState";

export { outboundTickState, type DeliveryTickState } from "./deliveryTickState";

interface DeliveryTicksProps {
  state: DeliveryTickState;
  className?: string;
  /** Hover text — the gate's reason on a simulated or blocked message. */
  title?: string;
}

/**
 * Delivery / seen indicator for an outbound message — Figma node 201:11782.
 *   - **sent:**        single ✓  in grey-500
 *   - **delivered:**   double ✓✓ in the org primary brand color
 *   - **seen:**        double ✓✓ in the org secondary brand color
 *   - **undelivered:** a break marker in yellow — deliberately NOT a tick,
 *     because part of the message never reached the recipient. On voice this
 *     is a barge-in: the caller cut the bot off, and the stored text is only
 *     the part they actually heard.
 *   - **failed:**      an alert marker in red — nothing was delivered at all
 *     (on voice, synthesis produced no audio and the caller heard silence).
 *   - **simulated:**   a crossed eye in purple — the Outbound Gate built and
 *     recorded the message and withheld it from the wire (ENG-3373).
 *   - **blocked:**     a ban marker in red — the person had opted out.
 *
 * Surface-agnostic: callers map their message's `deliveryStatus` + `seenBy` to
 * one of these states (see `agentTickState` / `userTickState` in MessageBubble,
 * and the voice-transcript branch of ConversationTimeline).
 */
export function DeliveryTicks({ state, className, title }: DeliveryTicksProps): React.ReactElement {
  if (state === "simulated") {
    return (
      <IoEyeOffOutline
        className={filterClasses("text-purple-600", className)}
        size={14}
        title={title}
        aria-label="Simulated — built and recorded, not sent"
      />
    );
  }
  if (state === "blocked") {
    return <IoBanOutline className={filterClasses("text-red-500", className)} size={14} title={title} aria-label="Blocked — the person opted out" />;
  }
  if (state === "undelivered") {
    return <IoRemoveCircleOutline className={filterClasses("text-yellow-600", className)} size={14} aria-label="Interrupted — partially delivered" />;
  }
  if (state === "failed") {
    return <IoAlertCircleOutline className={filterClasses("text-red-500", className)} size={14} aria-label="Failed — not delivered" />;
  }
  if (state === "sent") {
    return <IoCheckmark className={filterClasses("text-grey-500", className)} size={14} aria-label="Sent" />;
  }
  return (
    <IoCheckmarkDone
      className={filterClasses(state === "seen" ? "text-secondary" : "text-primary-500", className)}
      size={14}
      aria-label={state === "seen" ? "Seen" : "Delivered"}
    />
  );
}
